import { useCallback, useEffect, useMemo, useState } from "react";
import {
  deleteResidenceCategory,
  getResidenceCategory,
  ResidenceCategoryModel,
  updateResidenceCategory,
  UpdateResidenceCategoryRequest,
} from "../../clients/BackOfficeApi";
import { Box, Button, FormControl, FormLabel, Heading, Input, Spinner, Text } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useIsAuthenticated } from "@azure/msal-react";
import {
  ValidationErrorModel,
  isApiError,
  isBadRequest,
} from "../../utils/apiUtils";
import { getDynamicMessageId } from "../../components/I18n";
import { useIntl } from "react-intl";
import { ErrorList } from "../../elements/ErrorList";
import { DetailPageHeader } from "../../compositions/DetailPageHeader";
import { ActionButton } from "../../elements/ActionButton";
import { DeleteIcon } from "@chakra-ui/icons";
import { useDirty } from "../../contexts/DirtyContext";

export function ResidenceCategoryDetailPage() {
  const residenceCategoryId = Number(useParams().residenceCategoryId);
  const [validationError, setValidationError] =
    useState<ValidationErrorModel<UpdateResidenceCategoryRequest>>();
  const [error, setError] = useState<string>();
  const isAuthenticated = useIsAuthenticated();
  const intl = useIntl();
  const navigate = useNavigate();
  const [dirty, setDirty] = useDirty(true);

  const [loading, setLoading] = useState(true);

  const [residenceCategory, setResidenceCategory] = useState<ResidenceCategoryModel>();
  const [modifiedResidenceCategory, setModifiedResidenceCategory] =
    useState<UpdateResidenceCategoryRequest>();

  const isDirty = useMemo(() => {
    if (!residenceCategory || !modifiedResidenceCategory) return false;

    return residenceCategory.name !== modifiedResidenceCategory.name;
  }, [residenceCategory, modifiedResidenceCategory]);

  useEffect(() => {
    setDirty(isDirty);
  }, [isDirty, setDirty]);

  const fetchResidenceCategory = useCallback(
    async (signal?: AbortSignal) => {
      if (!isAuthenticated) return;

      setError(undefined);
      setLoading(true);

      if (!Number.isInteger(residenceCategoryId) || residenceCategoryId <= 0) {
        setError("Ongeldig woningcategorie ID");
        setLoading(false);
        return;
      }

      getResidenceCategory({
        path: { residenceCategoryId },
        signal,
        throwOnError: true,
      })
        .then(({ data }) => {
          setResidenceCategory(data);
          setModifiedResidenceCategory((c) => c ?? data);
        })
        .catch((e) => {
          if (signal?.aborted) return;

          setError(`Fout bij ophalen woningcategorie: ${e.message ?? String(e)}`);
        })
        .finally(() => setLoading(false));
    },
    [isAuthenticated, residenceCategoryId]
  );

  useEffect(() => {
    const abortController = new AbortController();

    fetchResidenceCategory(abortController.signal);

    return () => abortController.abort("Component unmounted or re-rendered");
  }, [fetchResidenceCategory]);

  const update = useCallback(() => {
    if (!isAuthenticated || !residenceCategory || !modifiedResidenceCategory || !dirty) return;

    setError(undefined);
    setValidationError(undefined);

    updateResidenceCategory({
      path: { residenceCategoryId },
      body: modifiedResidenceCategory,
      throwOnError: true,
    })
      .catch((e) => {
        if (isApiError(e)) {
          const errorId = getDynamicMessageId(e.title ?? "Unknown error");

          setError(
            intl.formatMessage({ id: errorId, defaultMessage: e.title ?? "Unknown error" })
          );

          if (isBadRequest<UpdateResidenceCategoryRequest>(e)) {
            setValidationError(e.errors);
          }
        } else {
          setError(`Fout bij bijwerken woningcategorie: ${e}`);
        }
      })
      .finally(() => {
        fetchResidenceCategory();
      });
  }, [
    isAuthenticated,
    residenceCategory,
    residenceCategoryId,
    modifiedResidenceCategory,
    dirty,
    fetchResidenceCategory,
    intl,
  ]);

  const fetchDeleteResidenceCategory = useCallback(
    (confirmed: boolean) => {
      if (!confirmed) return;

      deleteResidenceCategory({
        path: { residenceCategoryId },
        throwOnError: true,
      }).then(() => navigate(".."));
    },
    [navigate, residenceCategoryId]
  );

  return (
    <Box>
      <DetailPageHeader>
        <ActionButton
          color="red.500"
          confirmDescription="Weet je zeker dat je deze woningcategorie wilt verwijderen? Deze actie kan niet ongedaan gemaakt worden."
          confirmTitle="Verwijderen bevestigen"
          icon={<DeleteIcon />}
          isDestructive
          onClick={fetchDeleteResidenceCategory}
          title="Verwijderen"
        />
      </DetailPageHeader>
      {loading && <Spinner />}
      {error && <Text color="red.500">{error}</Text>}
      {residenceCategory && (
        <Box>
          <Heading>{residenceCategory.name}</Heading>
          {modifiedResidenceCategory && (
            <>
              <FormControl isInvalid={!!validationError?.Name} mt={4}>
                <FormLabel htmlFor="residenceCategory.name">Naam</FormLabel>
                <Input
                  id="residenceCategory.name"
                  isInvalid={!!validationError?.Name}
                  name="residenceCategory.name"
                  onChange={(e) =>
                    setModifiedResidenceCategory({
                      ...modifiedResidenceCategory,
                      name: e.target.value,
                    })
                  }
                  value={modifiedResidenceCategory.name}
                />
                <ErrorList errors={validationError?.Name} />
              </FormControl>
              <Button isDisabled={!dirty} mt={2} onClick={update}>
                Opslaan
              </Button>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
